import React, { useEffect, useState } from 'react';
import { MenuLink } from '../../../models/menu';
import { Brand } from '../../../lib/theme';
import { compose } from 'recompose';
import clsx from 'clsx';
import styled, { cx } from '../../../lib/styles';
import Icon from '../../presentational/Icon';
import _ from 'lodash';
import { flattenArray } from '../../../lib/arrayUtils';
import { trackEvent } from '../../../lib/ga';
import { CALL } from '../../../views/HolidayCheckout/lib/ga';
import { raiseEvent, TRIGGER_NAVBAR } from '../../../lib/gtm';
import navbarStyles from './Navbar_styles';
import { connect, useSelector } from 'react-redux';
import { TrackingDataState } from '../../../store/reducers/trackingData';
import { trackingDataSelector } from '../../../store/selectors';
import { navbarSelector } from '../../../views/Navbar/store/selectors';

export interface Logo {
  logo_image: string;
  display_as_active: boolean;
  link: {
    title: string;
    url: string;
  };
}
export interface NavbarProps {
  logo?: string;
  logos: Logo[];
  brand: Brand;
  links: {
    contactUsNav: MenuLink[];
    socialsNav: MenuLink[];
    mainNav: MenuLink[];
  };
  actions?: MenuLink[];
  trackingData?: any;
  styles?: any;
}

/**
 * This is the common Navbar component resposible for rendering the top navigation bar
 * It should draw data from Redux and render the top navigation bar because it's parent components
 * are responsible for loading the data into Redux
 * /views/Navbar/Navbar.tsx
 * /pages/Layout.tsx
 * @param props
 * @returns {JSX.Element}
 */
const Navbar = (props: NavbarProps) => {
  // console.log('Navbar', { ...props });
  const trackingData: TrackingDataState = useSelector(trackingDataSelector);
  const navbarData = useSelector(navbarSelector);

  const [showDropdown, setShowDropdown] = useState<number | undefined>(undefined);
  const [toggleMobileCollapsed, setToggleMobileCollapsed] = useState<boolean>(true);
  const [selectedMenuObjectId, setSelectedMenuObjectId] = useState<number>(0);

  useEffect(() => {
    console.log('Navbar useEffect', { trackingData, navbarData });
  }, [trackingData, navbarData]);
  
  const {
    logos,
    brand = 'travel360',
    links,
    styles,    
  } = props;

  useEffect(() => {
    console.log('[DEBUGGING] Navbar Component Props', { props });
  }, []);

  const DropdownMenu = ({
    linkItem,
    visibleParentMenuId,
  }: {
    linkItem: MenuLink;
    visibleParentMenuId: number | undefined;
  }) => {
    const [activeDropdownItem, setActiveDropdownItem] =
      useState<number | undefined>(undefined);
    const [showDropdownMenu, setShowDropdownMenu] = useState<boolean>(
      linkItem.id == visibleParentMenuId
    );

    return (
      <ul
        // **  debugging with data attributes **
        // data-linkItemId={linkItem.id}
        // data-visibleParentMenuId={visibleParentMenuId}
        // data-parentMenuId={linkItem.parentMenuId}
        className={clsx(`dropdown-menu`, {
          show: showDropdownMenu,
        })}
        key={linkItem.id}
        onMouseLeave={() => setTimeout(() => 
          setShowDropdownMenu(false),
          3000)}
      >
        {linkItem.links?.map((item, index) => {
          const linkItemHasLinks = item.links && item.links.length > 0;
          return (
            <li
              key={index}
              // data-id={item.id}
              // data-parent={linkItem.id}
              // data-activeDropdownItem={activeDropdownItem}
              className={clsx('', {
                [`dropdown ${styles.dropdown} ${linkItem.classes?.join(' ')}`]:
                  linkItemHasLinks,
              })}
            >
              <a
                className={clsx(`dropdown-item ${styles.dropdownItem}`, {
                  [`dropdown-toggle`]: linkItemHasLinks,
                })}
                data-toggle={linkItemHasLinks ? `dropdown` : undefined}
                aria-haspopup={linkItemHasLinks ? `true` : undefined}
                aria-expanded={
                  linkItemHasLinks
                    ? activeDropdownItem == linkItem.id
                    : undefined
                }
                href={linkItemHasLinks ? undefined : item.href}
                onClick={() =>
                  setActiveDropdownItem(
                    activeDropdownItem ? undefined : item.id
                  )
                }
              >
                {item.title == '&nbsp;' ? '' : item.title}
              </a>
              {linkItemHasLinks && (
                <DropdownMenu
                  linkItem={item}
                  visibleParentMenuId={activeDropdownItem}
                />
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  const flattenedMenuItems = flattenArray(links?.mainNav ?? []);

  const mobileMenuItems =
    selectedMenuObjectId == 0
      ? {
          title: '',
          href: undefined,
          object_id: 0,
          parentMenuId: 0,
          links: flattenedMenuItems.filter(
            item => item.parentMenuId == selectedMenuObjectId
          ),
        }
      : flattenedMenuItems.filter(
          item => item.object_id == selectedMenuObjectId
        )[0];

  // console.log('TASNavbar', links);

  const hideNavItemLabel = (item: MenuLink) => {
    return item.classes && item.classes.some(d => d == 'showNoLabel');
  };

  const MobileListItem = mobileItem => (
    <li
      data-objectid={mobileItem.object_id}
      data-parentobjectid={mobileItem.parentMenuId}
      onClick={() => {
        if (mobileItem.links && mobileItem.links.length > 0) {
          setSelectedMenuObjectId(mobileItem.object_id ?? 0);
        } else {
          //  only raise an event if there are no sub-menu items
          raiseEvent({ 
            event: TRIGGER_NAVBAR, 
            sasEventType: `NAVBAR_MOBILE_ITEM_CLICK: ${mobileMenuItems.title}`
          });
        }
      }}
    >
      <a
        href={
          mobileItem.links && mobileItem.links.length > 0
            ? undefined
            : mobileItem.href
        }
        className={`mobile-nav-item d-flex justify-content-between align-items-center`}
      >
        {mobileItem.title ? mobileItem.title : ' '}
        {mobileItem.links && mobileItem.links.length > 0 && (
          <Icon
            className={'las la-angle-right mr-2 font-weight-bold'}
            icon={null}
            label={mobileMenuItems.title}
            size={'m'}
          />
        )}
      </a>
    </li>
  );

  const SlideTrayContents = ({slideTrayMenuItems}) => {
    return (
      <li
        // data-navItemId={navItem.id}
        // data-showDropdown={showDropdown}
        className={cx(`nav-item`, slideTrayMenuItems.classes?.join(' '))}
      >
        {slideTrayMenuItems.object_id !== 0 && (
          <div
            className={cx(
              `d-flex justify-content-between align-items-center mobileBreadcrumb`,
              styles.mobileBreadcrumb
            )}
          >
            <a
              data-objectid={slideTrayMenuItems.object_id}
              data-parentobjectid={slideTrayMenuItems.parentMenuId}
              className={cx(`nav-link mobile-dropdown-toggle`)}
              // href={
              //   mobileMenu.links && mobileMenu.links.length > 0
              //     ? `#`
              //     : mobileMenu.href
              // }
              data-href={slideTrayMenuItems.href}
              onClick={() => {
                if (
                  slideTrayMenuItems.links &&
                  slideTrayMenuItems.links.length > 0
                ) {
                  setSelectedMenuObjectId(
                    slideTrayMenuItems.parentMenuId ?? 0
                  );
                }
              }}
            >
              {slideTrayMenuItems.title && (
                <Icon
                  className={'mr-2 las la-angle-left'}
                  icon={null}
                  label={slideTrayMenuItems.title}
                  size={'s'}
                />
              )}
              <div className={`breadcrumb-title`}>
                {slideTrayMenuItems.title}
              </div>
            </a>
          </div>
        )}
        <ul>
          {slideTrayMenuItems.links?.map((mobileItem, index) => (
            <MobileListItem key={index} {...mobileItem} />
          ))}
        </ul>
      </li>
    )
  };

  return brand === 'sas' ? (
    <>
      <button
        className={clsx(`navbar-toggler p-0 pr-1 ${styles.navbarToggler}`, {
          collapsed: toggleMobileCollapsed,
        })}
        style={{ backgroundColor: 'transparent !important' }}
        type="button"
        data-toggle="collapse"
        data-target="#TASNavbarContent"
        aria-controls="TASNavbarContent"
        aria-expanded={toggleMobileCollapsed ? `true` : `false`}
        aria-label="Toggle navigation"
        onClick={() => setToggleMobileCollapsed(!toggleMobileCollapsed)}
      >
        <span
          className={`las la-bars text-black navbar-toggler-icon d-flex align-items-center justify-content-center`}
          style={{ fontSize: '1.2em' }}
        ></span>
      </button>
      {mobileMenuItems && (
        <div
          className={clsx('slideTray d-none', styles.styleTray, {
            show: !toggleMobileCollapsed,
          })}
        >
          <div
            className={clsx(`navbarMobile`, styles.navbarMobile, {
              show: !toggleMobileCollapsed,
            })}
          >
            <div
              className={clsx(`navbarCollapse position-relative ${styles.navbarCollapse}`, {
                show: !toggleMobileCollapsed,
              })}
              id="TASNavbarContent"
            >
              <ul className={cx(`navMobile`, styles.navMobile)} style={{ boxShadow: '-7px 6px 7px 2px rgba(0, 0, 0, 0.3)' }}>
                <SlideTrayContents slideTrayMenuItems={mobileMenuItems} />
              </ul>
              <span 
                role={'button'}
                className={'position-absolute'}
                style={{ zIndex: 999999, }}
              >
                <Icon
                  className={'las la-times-circle'}
                  icon={null}
                  color={'black'}
                  label={'Close navigation'}
                  size={'xxxl'}
                  onClick={() => {
                    setToggleMobileCollapsed(true);
                    setSelectedMenuObjectId(0);
                  }}
                />
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  ) :
  (
    <header className={cx(`sas-page-header`, styles.sasPageHeader)}>
      <nav
        className={cx(
          `navbar navbar-light topNavigationBar`,
          styles.topNavigationBar,
          brand
        )}
      >
        <ul
          className={cx(
            `navbar-nav d-flex flex-row navLogos`,
            styles.navLogos
          )}
        >
          {logos && logos?.map((logo, index) => (
            <li
              key={index}
              className={clsx(`nav-item`, {
                active: logo.display_as_active,
              })}
            >
              <a 
                className={`nav-link px-3`} 
                href={logo.link.url}
                onClick={() => raiseEvent({ 
                  event: TRIGGER_NAVBAR, 
                  sasEventType: `NAVBAR_LOGO_CLICK: ${logo.link.title}`
                })}
              >
                <img
                  className={styles.brandLogo}
                  src={logo.logo_image}
                  alt={logo.link.title}
                  title={`Hi ${trackingData?.firstname ?? ''}! ${logo.link.title}`}
                />
              </a>
            </li>
          ))}
        </ul>
        {links?.contactUsNav?.length > 0 && (
        <ul
          className={cx(
            `navbar-nav d-flex flex-row navContact`,
            styles.navContact
          )}
        >
          {links.contactUsNav.map((navItem, index) => (
            <li className={`nav-item`} key={index}>
              <a
                className={`nav-link ${navItem.classes?.join(' ')}`}
                href={navItem.href}
                onClick={() => {
                  raiseEvent({ 
                    event: TRIGGER_NAVBAR, 
                    sasEventType: `NAVBAR_ITEM_CLICK: ${navItem.title}`
                  });
                  if (navItem.href.substring(0, 3) === 'tel') {
                    trackEvent(CALL);
                  }
                }}
              >
                {navItem.iconClass && (
                  <Icon
                    className={clsx(navItem.iconClass, {
                      'mr-2': !hideNavItemLabel(navItem),
                    })}
                    icon={null}
                    label={navItem.title}
                    size={hideNavItemLabel(navItem) ? 'xxl' : 'xl'}
                  />
                )}
                {hideNavItemLabel(navItem) ? '' : navItem.title}
              </a>
            </li>
          ))}
        </ul>)}
        {links?.socialsNav?.length > 0 && (<ul
          className={cx(
            `navbar-nav d-flex flex-row navSocials`,
            styles.navSocials
          )}
        >
          {links.socialsNav.map((navItem, index) => (
            <li className={`nav-item`} key={index}>
              <a
                className={`nav-link ${navItem.classes?.join(' ')}`}
                href={navItem.href}
                onClick={() => {
                  raiseEvent({ 
                    event: TRIGGER_NAVBAR, 
                    sasEventType: `NAVBAR_ITEM_CLICK: ${navItem.title}`
                  });
                }}
              >
                {navItem.iconClass && (
                  <Icon
                    className={clsx(navItem.iconClass, {
                      'mr-2': !hideNavItemLabel(navItem),
                    })}
                    icon={null}
                    label={navItem.title}
                    size={hideNavItemLabel(navItem) ? 'xxl' : 'xl'}
                  />
                )}
                {hideNavItemLabel(navItem) ? '' : navItem.title}
              </a>
            </li>
          ))}
        </ul>)}
        {links?.mainNav?.length > 0 && (
        <div className={cx(`navbar-nav navMain`, styles.navMain)}>
          <ul>
            {links.mainNav.map((navItem, index) => {
              return (
                <li
                  key={index}
                  // data-navItemId={navItem.id}
                  // data-showDropdown={showDropdown}
                  className={clsx(`nav-item ${navItem.classes?.join(' ')}`, {
                    dropdown: navItem.links && navItem.links.length > 0,
                  })}
                >
                  <a
                    className={clsx(`nav-link`, {
                      [`dropdown-toggle`]:
                        navItem.links && navItem.links.length > 0,
                    })}
                    data-toggle={
                      navItem.links && navItem.links.length > 0
                        ? `dropdown`
                        : undefined
                    }
                    aria-haspopup={
                      navItem.links && navItem.links.length > 0
                        ? `true`
                        : undefined
                    }
                    aria-expanded={
                      navItem.links && navItem.links.length > 0 && showDropdown
                        ? `true`
                        : undefined
                    }
                    href={
                      navItem.links && navItem.links.length > 0
                        ? undefined
                        : navItem.href
                    }
                    onClick={() => {
                      if (!navItem.links || navItem.links.length == 0) {
                        //  only raise an event if there are no sub-menu items
                        raiseEvent({ 
                          event: TRIGGER_NAVBAR, 
                          sasEventType: `NAVBAR_ITEM_CLICK: ${navItem.title}`
                        });
                      } 
                      setShowDropdown(showDropdown === navItem.id ? undefined : navItem.id);
                    }}
                  >
                    {navItem.iconClass && (
                      <Icon
                        className={clsx(navItem.iconClass, {
                          'mr-2': !hideNavItemLabel(navItem),
                        })}
                        icon={null}
                        label={navItem.title}
                        size={hideNavItemLabel(navItem) ? 'xxl' : 'xl'}
                      />
                    )}
                    {hideNavItemLabel(navItem) ? '' : navItem.title}
                  </a>
                  {navItem.links && navItem.links.length > 0 && (
                    <DropdownMenu
                      linkItem={navItem}
                      visibleParentMenuId={showDropdown}
                    />
                  )}
                </li>
              );
            })}
            <li className={`nav-item`}>
              <a
                className={clsx(`nav-link`, {})}
                href={`/?s=`}
                onClick={() => raiseEvent({ 
                  event: TRIGGER_NAVBAR, 
                  sasEventType: `NAVBAR_ITEM_CLICK: Search`
                  })
                }
              >
                <Icon
                  className={clsx(`las la-search mr-2`)}
                  icon={null}
                  label={'Search'}
                  size={'xxl'}
                />
              </a>
            </li>
          </ul>
        </div>
        )}
        <ul className={cx(`navbar-nav navSearch`, styles.navSearch)}>
          <li className={`nav-item`}>
            <a
              className={clsx(`nav-link`, {})}
              href={`/?s=`}
              onClick={() => raiseEvent({ 
                event: TRIGGER_NAVBAR, 
                sasEventType: `NAVBAR_ITEM_CLICK: Search`
                })
              }
            >
              <Icon
                className={clsx(`las la-search mr-2`)}
                icon={null}
                label={'Search'}
                size={'xxl'}
              />
            </a>
          </li>
        </ul>
        <ul className={cx(`navbar-nav navToggler`, styles.navToggler)}>
          <li>
            <button
              className={clsx(`navbar-toggler ${styles.navbarToggler}`, {
                collapsed: toggleMobileCollapsed,
              })}
              type="button"
              data-toggle="collapse"
              data-target="#TASNavbarContent"
              aria-controls="TASNavbarContent"
              aria-expanded={toggleMobileCollapsed ? `true` : `false`}
              aria-label="Toggle navigation"
              onClick={() => setToggleMobileCollapsed(!toggleMobileCollapsed)}
            >
              <span
                className={`las la-bars text-white navbar-toggler-icon ${styles.navbarTogglerIcon}`}
              ></span>
            </button>
          </li>
        </ul>
      </nav>

      {mobileMenuItems && (
        <div
          className={clsx('slideTray', styles.styleTray, {
            show: !toggleMobileCollapsed,
          })}
        >
          <div
            className={clsx(`navbarMobile`, styles.navbarMobile, {
              show: !toggleMobileCollapsed,
            })}
          >
            <div
              className={clsx(`navbarCollapse ${styles.navbarCollapse}`, {
                show: !toggleMobileCollapsed,
              })}
              id="TASNavbarContent"
            >
              <ul className={cx(`navMobile`, styles.navMobile)}>
                <li
                  // data-navItemId={navItem.id}
                  // data-showDropdown={showDropdown}
                  className={cx(`nav-item`, mobileMenuItems.classes?.join(' '))}
                >
                  {mobileMenuItems.object_id !== 0 && (
                    <div
                      className={cx(
                        `d-flex justify-content-between align-items-center mobileBreadcrumb`,
                        styles.mobileBreadcrumb
                      )}
                    >
                      <a
                        data-objectid={mobileMenuItems.object_id}
                        data-parentobjectid={mobileMenuItems.parentMenuId}
                        className={cx(`nav-link mobile-dropdown-toggle`)}
                        // href={
                        //   mobileMenu.links && mobileMenu.links.length > 0
                        //     ? `#`
                        //     : mobileMenu.href
                        // }
                        data-href={mobileMenuItems.href}
                        onClick={() => {
                          if (
                            mobileMenuItems.links &&
                            mobileMenuItems.links.length > 0
                          ) {
                            setSelectedMenuObjectId(
                              mobileMenuItems.parentMenuId ?? 0
                            );
                          }
                        }}
                      >
                        {mobileMenuItems.title && (
                          <Icon
                            className={'mr-2 las la-angle-left'}
                            icon={null}
                            label={mobileMenuItems.title}
                            size={'s'}
                          />
                        )}
                        <div className={`breadcrumb-title`}>
                          {mobileMenuItems.title}
                        </div>
                      </a>
                    </div>
                  )}
                  <ul>
                    {mobileMenuItems.links?.map((mobileItem, index) => (
                      <MobileListItem key={index} {...mobileItem} />
                    ))}
                  </ul>
                </li>
              </ul>
              <Icon
                className={'las la-times-circle'}
                icon={null}
                color={'white'}
                label={'Close navigation'}
                size={'xxxl'}
                onClick={() => {
                  setToggleMobileCollapsed(true);
                  setSelectedMenuObjectId(0);
                }}
              />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

const mapStateToProps = (state) => 
(
  { trackingData: state.trackingData }
);

export default compose(
  connect(mapStateToProps),
  styled(navbarStyles)
)(Navbar);